import React, { useEffect, useState, useMemo } from 'react';
import './AddMetersModal.css';
import Modal from './common/Modal';

const AddMetersModal = ({ isOpen, onClose, meters = [], service = null, onSave }) => {
  const [date, setDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [inputs, setInputs] = useState({});
  const [errors, setErrors] = useState({});

  // Determine meter count from service tariffs or existing meters
  const meterCount = useMemo(() => {
    // Используем количество тарифов как количество счетчиков
    if (service && Array.isArray(service.tariffs) && service.tariffs.length > 0) {
      return service.tariffs.length;
    }
    // Fallback to unique indices from meters
    const uniq = new Set(meters.map((m) => Number(m.index)).filter((x) => !isNaN(x)));
    return Math.max(uniq.size, 1);
  }, [service, meters]);

  // Compute previous/latest reading per index
  const prevReadings = useMemo(() => {
    const map = {};

    // Инициализируем null для всех индексов
    for (let i = 0; i < meterCount; i++) {
      map[i] = null;
    }

    // Проходим по всем метрам и берем последнее показание для каждого index
    meters.forEach((m) => {
      if (Array.isArray(m.readings)) {
        m.readings.forEach((reading) => {
          const idx = Number(reading.index);
          if (isNaN(idx)) return;

          // Берем показание с последней датой
          const mDate = new Date(m.date || m.createdAt || 0);
          const existing = map[idx];

          if (!existing || new Date(existing.date) < mDate) {
            map[idx] = {
              reading: Number(reading.reading),
              date: m.date || m.createdAt,
            };
          }
        });
      }
    });

    return map;
  }, [meters, meterCount]);

  useEffect(() => {
    if (isOpen) {
      // reset inputs when opening
      const init = {};
      for (let i = 0; i < meterCount; i++) init[i] = '';
      setInputs(init);
      const initErrors = {};
      for (let i = 0; i < meterCount; i++) initErrors[i] = '';
      setErrors(initErrors);
      // reset date to today
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [isOpen, meterCount]);

  const handleChange = (index, value) => {
    // allow only numbers
    if (value === '' || /^\d+$/.test(value)) {
      setInputs((prev) => ({ ...prev, [index]: value }));

      // validate against previous reading
      const prevVal = prevReadings[index]?.reading ?? null;
      if (value === '' || value === undefined) {
        setErrors((prevErr) => ({ ...prevErr, [index]: '' }));
      } else if (prevVal !== null && Number(value) < prevVal) {
        setErrors((prevErr) => ({
          ...prevErr,
          [index]: `Значение не может быть меньше предыдущего (${prevVal})`,
        }));
      } else {
        setErrors((prevErr) => ({ ...prevErr, [index]: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent submit if validation errors exist
    const hasErrors = Object.values(errors).some((err) => err && err.length > 0);
    if (hasErrors) {
      alert('Есть ошибки в заполнении показаний. Исправьте их перед сохранением.');
      return;
    }

    // Prepare array of readings to save
    const payload = [];
    for (let i = 0; i < meterCount; i++) {
      const raw = inputs[i];
      if (raw === undefined || raw === '') continue;
      const value = Number(raw);
      payload.push({ index: i, reading: value, date });
    }

    if (payload.length === 0) {
      alert('Введите хотя бы одно показание');
      return;
    }

    // call onSave with payload, parent responsible for API
    try {
      await onSave(payload, date);
      onClose();
    } catch (err) {
      console.error('Ошибка при сохранении показаний:', err);
      alert('Не удалось сохранить показания');
    }
  };

  // return numeric diff (null when no current value)
  const computeDiff = (index) => {
    const prev = prevReadings[index]?.reading ?? null;
    const cur = inputs[index] ? Number(inputs[index]) : null;
    if (cur === null || cur === '' || cur === undefined) return null;
    if (prev === null || prev === undefined) return cur;
    return cur - prev;
  };

  const formatDiff = (val) => {
    if (val === null || val === undefined) return '';
    return (val >= 0 ? '+' : '') + val;
  };

  const computePercentage = (index) => {
    const prev = prevReadings[index]?.reading ?? null;
    const cur = inputs[index] ? Number(inputs[index]) : null;
    if (cur === null || cur === '' || cur === undefined) return 0;
    if (prev === null || prev === undefined || prev === 0) return 100;
    return ((cur - prev) / totalSum) * 100;
  };

  const hasValidationErrors = Object.values(errors || {}).some((err) => err && err.length > 0);

  // Вычисляем итоговую сумму на основе текущих введенных значений
  const totalSum = useMemo(() => {
    if (!service || !Array.isArray(service.tariffs)) {
      return 0;
    }
    let sum = 0;
    for (let i = 0; i < meterCount; i++) {
      const diff = computeDiff(i);
      if (diff !== null && diff !== undefined) {
        // if (i < service.tariffs.length) {
        //   const tariff = service.tariffs[i];
        //   const value = tariff.value || 0;
        //   const coefficient = tariff.coefficient || 1;
        //   sum += diff * value * coefficient;
        // }
        sum += diff;
      }
    }
    return sum;
  }, [inputs, prevReadings, service, meterCount]);

  const computeTotalAmount = () => {
    if (!service || !Array.isArray(service.tariffs)) {
      return 0;
    }
    let amount = 0;
    for (let i = 0; i < meterCount; i++) {
      const diff = computeDiff(i);
      const sum = totalSum;
      if (diff !== null && diff !== undefined) {
        if (i < service.tariffs.length) {
          const tariff = service.tariffs[i];
          const value = tariff.value || 0;
          const coefficient = tariff.coefficient || 1;
          amount += sum * value * coefficient * computePercentage(i);
        }
      }
    }
    return amount;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overlayClassName="modal-overlay"
      contentClassName="modal-content"
    >
      <div className="modal-header">
        <h2>Добавить показания</h2>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="reading-date">Дата показаний</label>
          <input
            id="reading-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>

        <div className="meters-inputs">
          {Array.from({ length: meterCount }).map((_, i) => (
            <div className="meter-input-row" key={i}>
              <div className="meter-label">{service.tariffs[i].name}</div>
              <div className="meter-prev">Предыдущее: {prevReadings[i]?.reading ?? '-'}</div>
              <input
                className="meter-new-input"
                type="number"
                step="1"
                inputMode="numeric"
                pattern="\\d*"
                placeholder="Новое показание"
                value={inputs[i] || ''}
                onChange={(e) => handleChange(i, e.target.value)}
                min={prevReadings[i]?.reading}
              />
              <div
                className={`meter-diff ${(() => {
                  const v = computeDiff(i);
                  return v !== null && v < 0 ? 'negative' : '';
                })()}`}
              >
                <span className="diff-label">Разница:</span>
                <br />
                <span className="diff-value">{formatDiff(computeDiff(i))}</span>
                {errors[i] && <span className="tooltip">{errors[i]}</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="modal-sum-preview">
          <label>
            Итоговая сумма: <strong>{computeTotalAmount().toFixed(2)} грн</strong>
          </label>
        </div>

        <div className="modal-actions">
          <button type="button" className="button-secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            type="submit"
            className="button-primary"
            disabled={hasValidationErrors}
            title={hasValidationErrors ? 'Исправьте ошибки' : 'Сохранить'}
          >
            Сохранить
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddMetersModal;
